import React, { useState, useEffect } from 'react';
import { Mail, Send, Phone, MapPin } from 'lucide-react';
import './Contact.css';
import usePageTitle from '../hooks/usePageTitle';
import NewsletterSignup from '../components/NewsletterSignup';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const Contact: React.FC = () => {
  usePageTitle('Contact - The Kults');
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Auto-scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    const utm: { [key: string]: string } = {};
    
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    
    if (utmSource) utm.utm_source = utmSource;
    if (utmMedium) utm.utm_medium = utmMedium;
    if (utmCampaign) utm.utm_campaign = utmCampaign;
    
    return utm;
  };

  const getPageMetadata = () => ({
    source_page: window.location.href,
    referrer: document.referrer,
    page_title: document.title,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const utm = getUTMParams();
    const meta = getPageMetadata();

    try {
      const response = await fetch('https://us-central1-trinity-452312.cloudfunctions.net/thekults-contact-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          ...utm,
          ...meta,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        // Scroll to top when transitioning to success state
        window.scrollTo(0, 0);
      } else {
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: Mail,
      title: 'Management',
      description: 'For press inquiries, booking, and business opportunities',
      action: 'Send us a message'
    },
    {
      icon: Phone,
      title: 'Booking',
      description: 'For tour and event booking inquiries',
      action: 'Contact booking team'
    },
    {
      icon: MapPin,
      title: 'Location',
      description: 'Based in Basel, Switzerland',
      action: 'International touring'
    }
  ];

  if (isSubmitted) {
    return (
      <div className="contact-page">
        <section className="contact-hero">
          <div className="container">
            <h1>CONTACT</h1>
            <p>Get in touch with The Kults management</p>
          </div>
        </section>

        <section className="contact-success">
          <div className="container">
            <div className="success-message">
              <Mail size={48} />
              <h2>Message Sent Successfully!</h2>
              <p>Thank you for contacting The Kults. We'll get back to you as soon as possible.</p>
              <button 
                className="btn btn-primary"
                onClick={() => setIsSubmitted(false)}
              >
                Send Another Message
              </button>
            </div>
          </div>
        </section>

        <section className="newsletter-section">
          <div className="container">
            <NewsletterSignup 
              title="Stay Connected"
              description="Subscribe to get the latest news, tour announcements, and exclusive content from The Kults."
              variant="compact"
            />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <h1>CONTACT</h1>
          <p>Get in touch with The Kults management</p>
        </div>
      </section>

      <section className="contact-methods">
        <div className="container">
          <h2>Contact Information</h2>
          <div className="methods-grid">
            {contactMethods.map((method, index) => (
              <div key={index} className="method-card">
                <div className="method-icon">
                  <method.icon size={32} />
                </div>
                <h3>{method.title}</h3>
                <p>{method.description}</p>
                <span className="method-action">{method.action}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="contact-form-section">
        <div className="container">
          <div className="contact-layout">
            <div className="contact-info">
              <h2>Send Us a Message</h2>
              <p>
                For press inquiries, booking opportunities, business partnerships, 
                or any other professional matters, please use the form below. 
                We typically respond within 48 hours.
              </p>
              <div className="contact-details">
                <div className="detail-item">
                  <Mail size={20} />
                  <span>Contact form above</span>
                </div>
                <div className="detail-item">
                  <MapPin size={20} />
                  <span>Basel, Switzerland</span>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a subject</option>
                    <option value="press">Press Inquiry</option>
                    <option value="booking">Booking Request</option>
                    <option value="business">Business Partnership</option>
                    <option value="general">General Inquiry</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    placeholder="Please provide details about your inquiry..."
                  />
                </div>

                {error && <div className="form-error">{error}</div>}

                <button 
                  type="submit" 
                  className="btn btn-primary submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="newsletter-section">
        <div className="container">
          <NewsletterSignup 
            title="Stay Connected"
            description="Subscribe to get the latest news, tour announcements, and exclusive content from The Kults."
            variant="compact"
          />
        </div>
      </section>
    </div>
  );
};

export default Contact; 